<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>

  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'
import { connectSocket, disconnectSocket } from '@/libs/socket'
import { isUserLoggedIn } from '@/auth/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  mounted() {
    if (isUserLoggedIn()) this.initRealtime()
  },
  beforeDestroy() {
    disconnectSocket()
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  methods: {
    initRealtime() {
      const sock = connectSocket()
      if (!sock) return
      if (sock._traderioHandlersAttached) return
      sock._traderioHandlersAttached = true

      // ── Warning event'leri (loss_cover_stop, all_bots_losing, time_expired, drift, vs.) ──
      sock.on('target:warning', payload => {
        this.$store.dispatch('notifications/add', {
          type: this.warningVariant(payload.reason),
          reason: payload.reason,
          message: payload.message,
          source: { kind: 'target', id: payload.targetId },
          data: payload.data,
          timestamp: payload.timestamp,
        })
        this.fireToast(payload, 'target')
      })
      sock.on('bot:warning', payload => {
        this.$store.dispatch('notifications/add', {
          type: this.warningVariant(payload.reason),
          reason: payload.reason,
          message: payload.message,
          source: { kind: 'bot', id: payload.botId },
          data: payload.data,
          timestamp: payload.timestamp,
        })
        this.fireToast(payload, 'bot')
      })

      // ── Target lifecycle update — sadece transition (reason set ise) toast'la ──
      sock.on('target:update', payload => {
        if (!payload.reason) return // sadece PnL refresh, toast yok
        const { reason } = payload
        const text = reason === 'profit_hit'
          ? `Hedef #${payload.targetId} tamamlandı (kar hedefine ulaşıldı).`
          : reason === 'loss_hit'
            ? `Hedef #${payload.targetId} başarısız (zarar limiti aşıldı).`
            : reason === 'cycle_restart'
              ? `Hedef #${payload.targetId} yeni cycle başladı.`
              : `Hedef #${payload.targetId}: ${reason}`
        this.$store.dispatch('notifications/add', {
          type: reason === 'profit_hit' || reason === 'cycle_restart' ? 'success' : 'danger',
          reason,
          message: text,
          source: { kind: 'target', id: payload.targetId },
          timestamp: payload.timestamp,
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Hedef Güncellendi',
            icon: reason === 'profit_hit' ? 'AwardIcon' : 'AlertTriangleIcon',
            text,
            variant: reason === 'profit_hit' || reason === 'cycle_restart' ? 'success' : 'danger',
          },
        }, { timeout: 5000 })
      })
    },
    fireToast(payload, kind) {
      const variant = this.warningVariant(payload.reason)
      const icon = variant === 'danger' ? 'AlertOctagonIcon' : 'AlertCircleIcon'
      const title = kind === 'target' ? `Hedef #${payload.targetId}` : `Bot #${payload.botId}`
      this.$toast({
        component: ToastificationContent,
        props: {
          title, icon, text: payload.message, variant,
        },
      }, { timeout: 6000 })
    },
    warningVariant(reason) {
      const danger = ['loss_hit', 'all_bots_losing', 'all_losing_auto_stopped', 'time_expired']
      const warn = ['loss_cover_stop', 'manual_exchange_close', 'orphan_exchange_position']
      if (danger.includes(reason)) return 'danger'
      if (warn.includes(reason)) return 'warning'
      return 'info'
    },
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
    }
  },
}
</script>
